import { FundCardStyle } from './style';
import { FileCard } from '../FileCard';
import { useMemo, useRef } from 'react';
import { FiUpload } from 'react-icons/fi';
import { UnmappedFileCard } from '../UnmappedFileCard';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { PlaceholderBox } from '../PlaceholderBox';
import { SHEET_TOTAL_HEIGHT, getPlaceholderHeight } from '../../utils';
import { EmptyFileCard } from '../EmptyFileCard';
import reportStore from '../../store';
import { Button } from '@mantine/core';
import { DropZone } from '../../../../../components/DropZone';
import { PermissionGuard } from '../../../../../components/PermissionGuard';

export function FundCard({
  bulkFiles,
  fileIndex,
  currentFundName,
  previousFundName,
  handleAddMappedSheet,
  handleRemoveMappedSheet,
  onChooseSheet,
  handleSelectSource,
  handleUploadFundFiles,
  currentFundId,
  index,
  sources,
  handleExpandFile,
  expandedFileIds,
  onChangeFundMappings,
  handleTrackChanges,
  perFundFiles,
  handleDeleteFile,
  handleDeleteSheet,
  isInitialSetup,
  handleReplaceFile,
  handleUnmapFile,
  handleDownloadFile,
  handleAutoMatchSheets,
  handlePreventReset,
  refetch,
  hasInProgressFiles,
  isCustomReportType,
  acceptedFileTypes,
  acceptedDropzoneTypes,
  canManageWorkflow = true,
  workflowId,
  isUploadLoading = false,
  previousPeriodId,
  onOpenUploadFlow
}) {
  const ref = useRef();
  const { isLockedPeriod } = reportStore;

  const onOpenFileUpload = () => {
    if (onOpenUploadFlow) {
      onOpenUploadFlow({
        fundId: currentFundId,
        fundName: currentFundName,
        openLocalPicker: () => ref.current?.click()
      });
      return;
    }

    ref.current.click();
  };

  const swapFiles = (arr, sourceIndex, destinationIndex) => {
    let updatedFiles = [...arr];

    try {
      // Swap only the `currentFile` field
      const temp = updatedFiles[sourceIndex]?.currentFile;

      updatedFiles[sourceIndex].currentFile = updatedFiles[destinationIndex]?.currentFile;

      updatedFiles[destinationIndex].currentFile = temp;

      updatedFiles[sourceIndex].currentFile = {
        ...(updatedFiles[sourceIndex]?.currentFile || {}),
        notMappedSheets: [
          ...(updatedFiles?.[sourceIndex]?.currentFile?.notMappedSheets || []),
          ...(updatedFiles?.[sourceIndex]?.currentFile?.sheets || [])
        ]
      };

      updatedFiles[destinationIndex].currentFile = {
        ...updatedFiles[destinationIndex]?.currentFile,
        notMappedSheets: [
          ...(updatedFiles[destinationIndex]?.currentFile?.notMappedSheets || []),
          ...(updatedFiles[destinationIndex]?.currentFile?.sheets || [])
        ]
      };

      // make empty sheets after swaping places
      updatedFiles[sourceIndex].currentFile.sheets = [];
      updatedFiles[destinationIndex].currentFile.sheets = [];

      // check to existence of previous file , then change isMapped flag
      updatedFiles[sourceIndex].currentFile.isMapped = !!updatedFiles[sourceIndex].previousFile;
      updatedFiles[destinationIndex].currentFile.isMapped =
        !!updatedFiles[destinationIndex].previousFile;

      // remove files without previous & current file
      updatedFiles = [
        ...updatedFiles.filter(file => !!file.previousFile?.id || !!file.currentFile?.id)
      ];
    } catch (err) {
      console.log(err);
    }

    return updatedFiles;
  };

  const onDragEndBulk = result => {
    if (isLockedPeriod || !canManageWorkflow) return;
    const { destination, source } = result;

    // Exit if dropped outside or in the same position
    if (!destination || destination.index === source.index) {
      return;
    }

    const sourceIndex = source.index;
    const destinationIndex = destination.index;

    handleTrackChanges();
    onChangeFundMappings(prev => {
      if (!prev.length) return prev;

      return prev?.map(fund => {
        const updatedBulkFiles = swapFiles(fund.bulkFiles, sourceIndex, destinationIndex);

        return {
          ...fund,
          bulkFiles: updatedBulkFiles
        };
      });
    });
  };

  const onDragEndBulkSheet = (fileId, sheets) => {
    if (isLockedPeriod || !canManageWorkflow) return;
    handleTrackChanges();
    onChangeFundMappings(prev => {
      return prev.map(fund => {
        fund.bulkFiles.map(file => {
          if (file.currentFile?.id === fileId) {
            file.currentFile.sheets = [...sheets];
          }

          return file;
        });

        return fund;
      });
    });
  };

  const onDragEndPerFund = result => {
    if (isLockedPeriod || !canManageWorkflow) return;
    const { destination, source } = result;

    // Exit if dropped outside or in the same position
    if (!destination || destination.index === source.index) {
      return;
    }

    const sourceIndex = source.index - bulkFiles.length;
    const destinationIndex = destination.index - bulkFiles.length;

    handleTrackChanges();
    onChangeFundMappings(prev => {
      return prev.map(fund => {
        if (fund.currentFund.id === currentFundId) {
          let updatedPerFundFiles = swapFiles(fund.perFundFiles, sourceIndex, destinationIndex);

          return {
            ...fund,
            perFundFiles: updatedPerFundFiles
          };
        }

        // Return other funds unchanged
        return fund;
      });
    });
  };

  const filteredPerFundFiles = useMemo(() => {
    // return perFundFiles.filter((file) => file.previousFile);
    return perFundFiles;
  }, [perFundFiles]);

  return (
    <FundCardStyle className={isCustomReportType ? 'custom-report-type' : ''}>
      {!isCustomReportType && (
        <div className="item">
          <div className="fund-header">
            <p className="name">{index}</p>
          </div>
        </div>
      )}

      {/* Previous Period */}
      {previousFundName ? (
        <div className="item">
          {!isCustomReportType && (
            <div className="fund-header">
              <p className="name">{previousFundName}</p>
            </div>
          )}

          <div className="files">
            {bulkFiles?.map((item, index) => {
              const mockedSheetsCount =
                (item.currentFile?.sheets?.length || 0) - (item.previousFile?.sheets?.length || 0);
              const isLoading = item?.currentFile?.status === 'in progress';
              let placeholderHeight = getPlaceholderHeight(item);

              return item.previousFile ? (
                <FileCard
                  isCustomReportType={isCustomReportType}
                  handlePreventReset={handlePreventReset}
                  handleAutoMatchSheets={handleAutoMatchSheets}
                  isInitialSetup={isInitialSetup}
                  refetch={refetch}
                  previousFileId={item.previousFile?.id}
                  previousPeriodId={previousPeriodId}
                  key={index + '_previous_file_' + item.previousFile?.id}
                  handleDownloadFile={handleDownloadFile}
                  handleReplaceFile={handleReplaceFile}
                  name={item.previousFile?.name}
                  mappedSheets={item.previousFile?.sheets}
                  notMappedSheets={item.previousFile?.notMappedSheets}
                  isMultiUpload={!item.isPerFund}
                  hasPlusButton
                  showAmongLine
                  onOpenSheet={() => handleExpandFile(fileIndex + '_' + index)}
                  isOpenSheet={expandedFileIds.includes(fileIndex + '_' + index)}
                  sources={sources}
                  isLoading={isLoading}
                  extraRows={mockedSheetsCount > 0 ? mockedSheetsCount : 0}
                />
              ) : expandedFileIds.includes(fileIndex + '_' + index) ? (
                <PlaceholderBox
                  key={index + '_placeholder_box_' + item.previousFile?.id}
                  height={placeholderHeight + 'px'}
                  text={'This file was not submitted in the previous period'}
                />
              ) : (
                <div key={index + '_placeholder_' + item.previousFile?.id} style={{ height: 24 }} />
              );
            })}
          </div>

          {filteredPerFundFiles?.length > 0 && (
            <div className="files per-fund">
              {filteredPerFundFiles.map((item, index) => {
                const indx = index + bulkFiles.length;
                const mockedSheetsCount =
                  (item.currentFile?.sheets?.length || 0) -
                  (item.previousFile?.sheets?.length || 0);
                let placeholderHeight = getPlaceholderHeight(item);
                const isLoading = item?.currentFile?.status === 'in progress';

                return item.previousFile ? (
                  <FileCard
                    isCustomReportType={isCustomReportType}
                    handlePreventReset={handlePreventReset}
                    handleAutoMatchSheets={handleAutoMatchSheets}
                    isInitialSetup={isInitialSetup}
                    refetch={refetch}
                    previousFileId={item.previousFile?.id}
                    previousPeriodId={previousPeriodId}
                    handleDownloadFile={handleDownloadFile}
                    key={index + '_previous_file_' + item.previousFile?.id}
                    handleReplaceFile={handleReplaceFile}
                    name={item.previousFile?.name}
                    mappedSheets={item.previousFile?.sheets}
                    notMappedSheets={item.previousFile?.notMappedSheets}
                    isMultiUpload={!item.isPerFund}
                    hasPlusButton
                    showAmongLine
                    isLoading={isLoading}
                    onOpenSheet={() => handleExpandFile(fileIndex + '_' + indx)}
                    isOpenSheet={expandedFileIds.includes(fileIndex + '_' + indx)}
                    hasLine
                    sources={sources}
                    extraRows={mockedSheetsCount > 0 ? mockedSheetsCount : 0}
                    acceptedFileTypes={acceptedFileTypes}
                  />
                ) : (
                  expandedFileIds.includes(fileIndex + '_' + indx) && (
                    <PlaceholderBox
                      key={index + '_placeholder_box_' + item.previousFile?.id}
                      height={placeholderHeight + 'px'}
                      text={'This file was not submitted in the previous period'}
                    />
                  )
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="item no-item">This fund was not submitted in the previous period</div>
      )}

      {/* Current Period */}
      <div className="item">
        {!isCustomReportType && (
          <div className="fund-header">
            <input
              ref={ref}
              type="file"
              style={{ display: 'none' }}
              onChange={e => handleUploadFundFiles(e, currentFundId)}
              multiple
              accept={acceptedFileTypes}
            />

            <p className="name">{currentFundName}</p>

            {!isLockedPeriod && (
              <DropZone
                onDrop={files => handleUploadFundFiles({ target: { files } }, currentFundId)}
                variant="compact"
                disabled={!canManageWorkflow}
                disableClick
                accept={acceptedDropzoneTypes}
              >
                <PermissionGuard allowed={canManageWorkflow}>
                  <Button
                    className="upload-file-btn"
                    variant="transparent"
                    onClick={handlePreventReset(onOpenFileUpload)}
                    size="xs"
                    disabled={!canManageWorkflow}
                    leftSection={<FiUpload />}
                  >
                    Upload Files
                  </Button>
                </PermissionGuard>
              </DropZone>
            )}
          </div>
        )}

        <DragDropContext onDragEnd={onDragEndBulk}>
          {/*
            `autoScrollerOptions={{ disabled: true }}` — react-beautiful-dnd's
            default auto-scroller walks up from the dragged item to the nearest
            scrollable ancestor. On this page there is no scroll container
            around the fund's file list, so the auto-scroller escapes all the
            way to the document root and scrolls the entire page as soon as
            the pointer nears the fund's visual edge. Disabling the auto-
            scroller keeps the page still during drag; reordering itself
            still works because the drop resolves against the Droppable's
            live client rect.
          */}
          <Droppable
            droppableId={currentFundId + '_files-list'}
            autoScrollerOptions={{ disabled: true }}
          >
            {provided => (
              <div className="files" {...provided.droppableProps} ref={provided.innerRef}>
                {bulkFiles?.map((item, index) => {
                  const mockedSheetsCount =
                    (item.previousFile?.sheets?.length || 0) -
                    (item.currentFile?.sheets?.length || 0);

                  const isLoading = item?.currentFile?.status === 'in progress';
                  const isOpenSheet = expandedFileIds.includes(fileIndex + '_' + index);

                  let placeholderHeight = getPlaceholderHeight(item, true);

                  return (
                    <Draggable
                      key={index + '_current_file_' + item.currentFile?.id}
                      draggableId={(item.currentFile?.id || index) + '_' + currentFundId}
                      index={index}
                    >
                      {(dragProvided, snapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          style={{
                            ...dragProvided.draggableProps.style,
                            opacity: snapshot.isDragging && 0.5,
                            // `width: 100%` survives the `position: fixed`
                            // switch react-beautiful-dnd makes on drag start.
                            // `flex: 1` (flex-grow: 1, flex-basis: 0%) would
                            // collapse the dragged element to zero width once
                            // it's pulled out of flex flow, dragging its
                            // siblings sideways.
                            width: '100%'
                          }}
                        >
                          {!item.currentFile && !bulkFiles.length && (
                            <PlaceholderBox
                              {...dragProvided.dragHandleProps}
                              backgroundColor="#fff"
                              height={(isOpenSheet ? placeholderHeight : SHEET_TOTAL_HEIGHT) + 'px'}
                              text='No files have been added for this file yet.<br/>Upload files by clicking the "Upload Files" button.'
                            />
                          )}

                          {!item.currentFile?.id && bulkFiles.length && (
                            <EmptyFileCard
                              isCustomReportType={isCustomReportType}
                              currentFileId={item.currentFile?.id}
                              name={item.currentFile?.name}
                              isMultiUpload
                              isOpenSheet={expandedFileIds.includes(fileIndex + '_' + index)}
                              showDrag={bulkFiles.length > 1}
                              isCurrentFund
                              isLoading={isLoading || hasInProgressFiles}
                              sources={sources}
                              dragProps={dragProvided.dragHandleProps}
                              extraRows={mockedSheetsCount > 0 ? mockedSheetsCount + 1 : 0}
                            />
                          )}

                          {item.currentFile?.isMapped
                            ? !!item.currentFile?.id && (
                                <FileCard
                                  isCustomReportType={isCustomReportType}
                                  handlePreventReset={handlePreventReset}
                                  handleAutoMatchSheets={handleAutoMatchSheets}
                                  isInitialSetup={isInitialSetup}
                                  refetch={refetch}
                                  previousFileId={item.previousFile?.id}
                                  handleDownloadFile={handleDownloadFile}
                                  name={item.currentFile?.name}
                                  handleReplaceFile={handleReplaceFile}
                                  mappedSheets={item.currentFile?.sheets}
                                  currentFileId={item.currentFile?.id}
                                  isOpenSheet={isOpenSheet}
                                  fileMappingId={item.currentFile?.id}
                                  onDeleteFile={handleDeleteFile}
                                  onDeleteSheet={handleDeleteSheet}
                                  isCurrentFund
                                  showAmongLineReverse={!isInitialSetup}
                                  showDrag={!isInitialSetup && bulkFiles.length > 1}
                                  handleUnmapFile={handleUnmapFile}
                                  showPlusButton={
                                    !isInitialSetup && canManageWorkflow && !isLockedPeriod
                                  }
                                  canManageWorkflow={canManageWorkflow}
                                  sources={sources}
                                  dragProps={dragProvided.dragHandleProps}
                                  isMultiUpload
                                  notMappedSheets={item.currentFile?.notMappedSheets}
                                  previousSheets={item.previousFile?.sheets || []}
                                  isLoading={isLoading || hasInProgressFiles}
                                  onChooseSheet={(sheetIndex, value, source) =>
                                    onChooseSheet(
                                      currentFundId,
                                      item.currentFile?.id,
                                      sheetIndex,
                                      value,
                                      source
                                    )
                                  }
                                  onOpenSheet={() => handleExpandFile(fileIndex + '_' + index)}
                                  onChangeSheets={_sheets =>
                                    onDragEndBulkSheet(item.currentFile.id, _sheets)
                                  }
                                  handleAddMappedSheet={() =>
                                    handleAddMappedSheet(currentFundId, item.currentFile?.id)
                                  }
                                  extraRows={mockedSheetsCount > 0 ? mockedSheetsCount : 0}
                                  acceptedFileTypes={acceptedFileTypes}
                                  handleSelectSource={(sheetId, value) =>
                                    handleSelectSource(item.currentFile?.id, sheetId, value)
                                  }
                                  onOpenUploadFlow={onOpenUploadFlow}
                                />
                              )
                            : !!item.currentFile?.id && (
                                <UnmappedFileCard
                                  isCustomReportType={isCustomReportType}
                                  canManageWorkflow={canManageWorkflow}
                                  refetch={refetch}
                                  handleDownloadFile={handleDownloadFile}
                                  handleReplaceFile={handleReplaceFile}
                                  mappedSheets={item.currentFile?.sheets}
                                  currentFileId={item.currentFile?.id}
                                  name={item.currentFile?.name}
                                  isMultiUpload
                                  onOpenSheet={() => handleExpandFile(fileIndex + '_' + index)}
                                  isOpenSheet={expandedFileIds.includes(fileIndex + '_' + index)}
                                  showDrag={bulkFiles.length > 1}
                                  onDeleteFile={handleDeleteFile}
                                  onDeleteSheet={handleDeleteSheet}
                                  isLoading={isLoading || hasInProgressFiles}
                                  onChooseSheet={(sheetIndex, value) =>
                                    onChooseSheet(
                                      currentFundId,
                                      item.currentFile?.id,
                                      sheetIndex,
                                      value
                                    )
                                  }
                                  notMappedSheets={item.currentFile?.notMappedSheets}
                                  handleRemoveMappedSheet={sheetIndex =>
                                    handleRemoveMappedSheet(
                                      currentFundId,
                                      item.currentFile?.id,
                                      sheetIndex
                                    )
                                  }
                                  handleAddMappedSheet={() =>
                                    handleAddMappedSheet(currentFundId, item.currentFile?.id)
                                  }
                                  handleSelectSource={(sheetId, value) =>
                                    handleSelectSource(item.currentFile?.id, sheetId, value)
                                  }
                                  fileMappingId={item.currentFile?.id}
                                  isCurrentFund
                                  sources={sources}
                                  dragProps={dragProvided.dragHandleProps}
                                  extraRows={mockedSheetsCount > 0 ? mockedSheetsCount : 0}
                                  acceptedFileTypes={acceptedFileTypes}
                                  onOpenUploadFlow={onOpenUploadFlow}
                                />
                              )}
                        </div>
                      )}
                    </Draggable>
                  );
                })}

                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {filteredPerFundFiles?.length > 0 && (
          <DragDropContext onDragEnd={onDragEndPerFund}>
            {/* Same rationale as the bulk Droppable above — keep the page
                still during drag by disabling react-beautiful-dnd's
                auto-scroller. */}
            <Droppable
              droppableId={currentFundId + '_files-list'}
              autoScrollerOptions={{ disabled: true }}
            >
              {provided => (
                <div
                  className="files per-fund"
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {filteredPerFundFiles.map((item, index) => {
                    const indx = index + bulkFiles.length;
                    const mockedSheetsCount =
                      (item.previousFile?.sheets?.length || 0) -
                      (item.currentFile?.sheets?.length || 0);
                    const isLoading = item?.currentFile?.status === 'in progress';

                    const isOpenSheet = expandedFileIds.includes(fileIndex + '_' + indx);

                    let placeholderHeight = getPlaceholderHeight(item, true);

                    return (
                      <Draggable
                        key={index + '_per_fund_current_file_' + item.currentFile?.id}
                        draggableId={(item.currentFile?.id || indx) + '_' + currentFundId}
                        index={indx}
                      >
                        {(dragProvided, snapshot) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            style={{
                              ...dragProvided.draggableProps.style,
                              opacity: snapshot.isDragging && 0.5,
                              // See sibling Draggable above for rationale — using
                              // `width: 100%` instead of `flex: 1` so the item
                              // keeps its full width once react-beautiful-dnd
                              // flips it to `position: fixed` on drag start.
                              width: '100%'
                            }}
                          >
                            {!item.currentFile && !perFundFiles.length && (
                              <PlaceholderBox
                                backgroundColor="#fff"
                                height={
                                  (isOpenSheet ? placeholderHeight : SHEET_TOTAL_HEIGHT) + 'px'
                                }
                                text='No files have been added for this file yet.<br/>Upload files by clicking the "Upload Files" button.'
                              />
                            )}

                            {!item.currentFile?.id && perFundFiles.length && (
                              <EmptyFileCard
                                isCustomReportType={isCustomReportType}
                                currentFileId={item.currentFile?.id}
                                name={item.currentFile?.name}
                                isOpenSheet={isOpenSheet}
                                showDrag={perFundFiles.length > 1}
                                isCurrentFund
                                sources={sources}
                                isLoading={isLoading || hasInProgressFiles}
                                dragProps={dragProvided.dragHandleProps}
                                extraRows={mockedSheetsCount > 0 ? mockedSheetsCount + 1 : 0}
                              />
                            )}

                            {item.currentFile?.isMapped
                              ? !!item.currentFile?.id && (
                                  <FileCard
                                    isCustomReportType={isCustomReportType}
                                    handlePreventReset={handlePreventReset}
                                    handleAutoMatchSheets={handleAutoMatchSheets}
                                    isInitialSetup={isInitialSetup}
                                    refetch={refetch}
                                    previousFileId={item.previousFile?.id}
                                    handleDownloadFile={handleDownloadFile}
                                    mappedSheets={item.currentFile?.sheets}
                                    handleReplaceFile={handleReplaceFile}
                                    notMappedSheets={item.currentFile?.notMappedSheets}
                                    handleUnmapFile={handleUnmapFile}
                                    name={item.currentFile?.name}
                                    previousSheets={item.previousFile?.sheets || []}
                                    isMultiUpload={false}
                                    isLoading={isLoading || hasInProgressFiles}
                                    onChooseSheet={(sheetIndex, value, source) =>
                                      onChooseSheet(
                                        currentFundId,
                                        item.currentFile?.id,
                                        sheetIndex,
                                        value,
                                        source
                                      )
                                    }
                                    currentFileId={item.currentFile?.id}
                                    onOpenSheet={() => handleExpandFile(fileIndex + '_' + indx)}
                                    isOpenSheet={isOpenSheet}
                                    fileMappingId={item.currentFile?.id}
                                    onDeleteFile={handleDeleteFile}
                                    onDeleteSheet={handleDeleteSheet}
                                    isCurrentFund
                                    showAmongLineReverse={!isInitialSetup}
                                    showDrag={!isInitialSetup && filteredPerFundFiles.length > 1}
                                    showPlusButton={
                                      !isInitialSetup && canManageWorkflow && !isLockedPeriod
                                    }
                                    canManageWorkflow={canManageWorkflow}
                                    sources={sources}
                                    dragProps={dragProvided.dragHandleProps}
                                    handleSelectSource={(sheetId, value) =>
                                      handleSelectSource(item.currentFile?.id, sheetId, value)
                                    }
                                    handleAddMappedSheet={() =>
                                      handleAddMappedSheet(currentFundId, item.currentFile?.id)
                                    }
                                    extraRows={mockedSheetsCount > 0 ? mockedSheetsCount : 0}
                                    acceptedFileTypes={acceptedFileTypes}
                                    onOpenUploadFlow={onOpenUploadFlow}
                                  />
                                )
                              : !!item.currentFile?.id && (
                                  <UnmappedFileCard
                                    isCustomReportType={isCustomReportType}
                                    canManageWorkflow={canManageWorkflow}
                                    refetch={refetch}
                                    handleDownloadFile={handleDownloadFile}
                                    handleReplaceFile={handleReplaceFile}
                                    mappedSheets={item.currentFile?.sheets}
                                    notMappedSheets={item.currentFile?.notMappedSheets}
                                    name={item.currentFile?.name}
                                    isOpenSheet={isOpenSheet}
                                    onOpenSheet={() => handleExpandFile(fileIndex + '_' + indx)}
                                    currentFileId={item.currentFile?.id}
                                    onDeleteFile={handleDeleteFile}
                                    onDeleteSheet={handleDeleteSheet}
                                    isLoading={isLoading || hasInProgressFiles}
                                    onChooseSheet={(sheetIndex, value) =>
                                      onChooseSheet(
                                        currentFundId,
                                        item?.currentFile?.id,
                                        sheetIndex,
                                        value
                                      )
                                    }
                                    handleRemoveMappedSheet={sheetIndex =>
                                      handleRemoveMappedSheet(
                                        currentFundId,
                                        item?.currentFile?.id,
                                        sheetIndex
                                      )
                                    }
                                    handleAddMappedSheet={() =>
                                      handleAddMappedSheet(currentFundId, item.currentFile?.id)
                                    }
                                    handleSelectSource={(sheetId, value) =>
                                      handleSelectSource(item.currentFile?.id, sheetId, value)
                                    }
                                    showDrag={filteredPerFundFiles.length > 1}
                                    sources={sources}
                                    isCurrentFund
                                    dragProps={dragProvided.dragHandleProps}
                                    extraRows={mockedSheetsCount > 0 ? mockedSheetsCount : 0}
                                    acceptedFileTypes={acceptedFileTypes}
                                    onOpenUploadFlow={onOpenUploadFlow}
                                  />
                                )}
                          </div>
                        )}
                      </Draggable>
                    );
                  })}

                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>
    </FundCardStyle>
  );
}
